"use client";

import { LanguageContext } from "@/context/languageContext";
import styles from "@/styles/modules/components/footer.module.scss";
import { MenuItem } from "@/types/wordpress-parts";
import { FooterOptionsQuery } from "@/types/wordpressQueries";
import Image from "next/image";
import Link from "next/link";
import { useContext } from "react";
import NewsletterForm from "./NewsletterForm";

interface FooterProps {
  footerMenuEn: MenuItem[];
  footerMenuDe: MenuItem[];
  footerOptions: FooterOptionsQuery;
}

export default function Footer({
  footerMenuEn,
  footerMenuDe,
  footerOptions,
}: FooterProps) {
  const { language } = useContext(LanguageContext);

  return (
    <footer className={styles.footer}>
      <div className={styles.footerContainer}>
        <section className={styles.logo} data-aos="fade-in">
          <Link href={`${language === "en" ? "/en" : "/de"}`}>
            <Image
              src="/images/imperial-logo.svg"
              alt="Imperial Riding School logo"
              width="129"
              height="95"
            />
          </Link>
        </section>
        <section className={styles.address} data-aos="fade-in" data-aos-delay="100">
          <ul>
            {language === "en"
              ? footerOptions.footerOptions.optionsFooter.address1 && (
                <li>
                  <Link
                    href={
                      footerOptions.footerOptions.optionsFooter.addressLink ||
                      "#"
                    }
                    target="_blank"
                  >
                    {footerOptions.footerOptions.optionsFooter.address1}
                  </Link>
                </li>
              )
              : footerOptions.footerOptionsDE.optionsFooter.address1 && (
                <li>
                  <Link
                    href={
                      footerOptions.footerOptionsDE.optionsFooter.addressLink ||
                      "#"
                    }
                    target="_blank"
                  >
                    {footerOptions.footerOptionsDE.optionsFooter.address1}
                  </Link>
                </li>
              )}
            {language === "en"
              ? footerOptions.footerOptions.optionsFooter.address2 && (
                <li><Link
                  href={
                    footerOptions.footerOptions.optionsFooter.addressLink ||
                    "#"
                  }
                  target="_blank"
                >

                  {footerOptions.footerOptions.optionsFooter.address2}

                </Link>
                </li>
              )
              : footerOptions.footerOptionsDE.optionsFooter.address2 && (
                <li>
                  <Link
                    href={
                      footerOptions.footerOptionsDE.optionsFooter.addressLink ||
                      "#"
                    }
                    target="_blank"
                  >
                    {footerOptions.footerOptionsDE.optionsFooter.address2}
                  </Link>
                </li>
              )}
            {language === "en"
              ? footerOptions.footerOptions.optionsFooter.phone && (
                <li>
                  <Link
                    href={`tel:${footerOptions.footerOptions.optionsFooter.phone.replaceAll(
                      " ",
                      ""
                    )}`}
                  >
                    {footerOptions.footerOptions.optionsFooter.phone}
                  </Link>
                </li>
              )
              : footerOptions.footerOptionsDE.optionsFooter.phone && (
                <li>
                  <Link
                    href={`tel:${footerOptions.footerOptionsDE.optionsFooter.phone.replaceAll(
                      " ",
                      ""
                    )}`}
                  >
                    {footerOptions.footerOptionsDE.optionsFooter.phone}
                  </Link>
                </li>
              )}
            {language === "en"
              ? footerOptions.footerOptions.optionsFooter.email && (
                <li>
                  <Link
                    href={`mailto:${footerOptions.footerOptions.optionsFooter.email}`}
                  >
                    {footerOptions.footerOptions.optionsFooter.email}
                  </Link>
                </li>
              )
              : footerOptions.footerOptionsDE.optionsFooter.email && (
                <li>
                  <Link
                    href={`mailto:${footerOptions.footerOptionsDE.optionsFooter.email}`}
                  >
                    {footerOptions.footerOptionsDE.optionsFooter.email}
                  </Link>
                </li>
              )}
          </ul>
        </section>
        <section className={styles.menu} data-aos="fade-in" data-aos-delay="200">
          <nav>
            <ul>
              {language === "en"
                ? footerMenuEn.map((menuItem) => (
                  <li key={menuItem.id}>
                    <Link href={menuItem.path} target={menuItem.target}>{menuItem.label}</Link>
                  </li>
                ))
                : footerMenuDe.map((menuItem) => (
                  <li key={menuItem.id}>
                    <Link href={menuItem.path} target={menuItem.target}>{menuItem.label}</Link>
                  </li>
                ))}
            </ul>
          </nav>
        </section>
        <section className={styles.newsletter} data-aos="fade-in" data-aos-delay="300">
          <NewsletterForm />
        </section>
        <section className={styles.links} data-aos="fade-in" data-aos-delay="400">
          <div className={styles.logos}>
            <Link href="https://www.eventhotels.com/en/" target="_blank">
              <Image
                alt="Event Hotels logo"
                src="/images/eh-logo.svg"
                width={55}
                height={25}
              />
            </Link>
            <Link
              href="https://www.marriott.com/en-gb/loyalty.mi"
              target="_blank"
            >
              <Image
                alt="Marriot Bonvoy logo"
                src="/images/marriott-bonvoy-logo.svg"
                width={82}
                height={24}
              />
            </Link>
            <Link href={language === 'en' ? 'https://autograph-hotels.marriott.com/' : 'https://autograph-hotels.marriott.com/de-DE/'} target="_blank">
              <Image
                alt="Autograph logo"
                src="/images/autograph-logo.svg"
                width={93}
                height={28}
              />
            </Link>
          </div>
          <div className={styles.socials}>
            {language === "en"
              ? footerOptions.footerOptions.optionsFooter.instagram && (
                <Link
                  href={footerOptions.footerOptions.optionsFooter.instagram}
                  target="_blank"
                >
                  <Image
                    alt="Instagram logo"
                    src="/images/instagram-logo.svg"
                    width={30}
                    height={30}
                  />
                </Link>
              )
              : footerOptions.footerOptionsDE.optionsFooter.instagram && (
                <Link
                  href={footerOptions.footerOptionsDE.optionsFooter.instagram}
                  target="_blank"
                >
                  <Image
                    alt="Instagram logo"
                    src="/images/instagram-logo.svg"
                    width={30}
                    height={30}
                  />
                </Link>
              )}
            {language === "en"
              ? footerOptions.footerOptions.optionsFooter.facebook && (
                <Link
                  href={footerOptions.footerOptions.optionsFooter.facebook}
                  target="_blank"
                >
                  <Image
                    alt="Facebook logo"
                    src="/images/facebook-logo.svg"
                    width={30}
                    height={30}
                  />
                </Link>
              )
              : footerOptions.footerOptionsDE.optionsFooter.facebook && (
                <Link
                  href={footerOptions.footerOptionsDE.optionsFooter.facebook}
                  target="_blank"
                >
                  <Image
                    alt="Facebook logo"
                    src="/images/facebook-logo.svg"
                    width={30}
                    height={30}
                  />
                </Link>
              )}
            {language === "en"
              ? footerOptions.footerOptions.optionsFooter.linkedin && (
                <Link
                  href={footerOptions.footerOptions.optionsFooter.linkedin}
                  target="_blank"
                >
                  <Image
                    alt="Linkedin logo"
                    src="/images/linkedin-logo.svg"
                    width={30}
                    height={30}
                  />
                </Link>
              )
              : footerOptions.footerOptionsDE.optionsFooter.linkedin && (
                <Link
                  href={footerOptions.footerOptionsDE.optionsFooter.linkedin}
                  target="_blank"
                >
                  <Image
                    alt="Linkedin logo"
                    src="/images/linkedin-logo.svg"
                    width={30}
                    height={30}
                  />
                </Link>
              )}
            {language === "en" ? (
              <Link href="/en/pages/reviews">
                <iframe src='https://api.trustyou.com/hotels/3bcba1d2-10f4-4b1c-afd9-7872166b7c87/trust_score.html?lang=en&key=a0993ac2-96c2-4ac9-8e32-5b30b0117bc4&size=s&scale=5&custom_style={"primaryColor":"0x231f20","textColor":"0x231f20"}'
                  height="40"
                  width="120"
                  style={{ border: 'none' }}></iframe>
              </Link>
            ) : (
              <Link href="/de/pages/bewertungen">
                <iframe src='https://api.trustyou.com/hotels/3bcba1d2-10f4-4b1c-afd9-7872166b7c87/trust_score.html?lang=de&key=a0993ac2-96c2-4ac9-8e32-5b30b0117bc4&size=s&scale=5&custom_style={"primaryColor":"0x231f20","textColor":"0x231f20"}'
                  height="40"
                  width="170"
                  style={{ border: 'none' }}></iframe>
              </Link>
            )}
          </div>
        </section>
      </div >
    </footer >
  );
}
